import { useMemo } from "react";
import { Sparkles } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Notice } from "@/components/ui/notice";
import ProgressBar from "@/components/shared/ProgressBar";
import {
  PrizeCard, HandedOutStat, StudentLink, HandOutRow, HandOutAllButton,
} from "@/components/schools/prizes/PrizePieces";
import { formatPrizeAmount, topInClassLabel, topStudentGroupWhere } from "@/lib/prizes";
import { ordinal } from "@/lib/timeAgo";
import { formatNumber, percentOf, pluralize } from "@/lib/format";

/**
 * A classroom's student prizes: who's earned what, who's still waiting
 * for it, and "Mark handed out". Built from the same pieces as the
 * school's prize report so it works the same for teachers and admins.
 *
 * Props:
 *   prizes: [{
 *     id, prize, kind, threshold_amount, award_style,
 *     achieved_count, disbursed_count, total_entries, entrants_count,
 *     disburse_all_path?,  // optional: hand it out to everyone waiting
 *     achievements: [{ id, student_name, student_path, achieved_at, disbursed, disbursed_at,
 *                      disbursed_by, entry_count, won, disburse_path, undisburse_path, certificate_path }],
 *     // top_in_class only (leaders and class_best name only this class's students):
 *     competes_within, group_label, finalized, leader_amount, leaders: [{ id, student_name, student_path }],
 *     leading_elsewhere, class_best: { students, amount, rank, gap_to_first } | null
 *   }]
 */
export default function PrizeTrackerCard({ prizes }) {
  const waiting = useMemo(
    () => (prizes || []).reduce((sum, p) => sum + p.achievements.filter((a) => !a.disbursed && (p.award_style !== "raffle" || a.won)).length, 0),
    [prizes]
  );

  if (!prizes?.length) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Student prizes
          </CardTitle>
          <CardDescription>
            When a student earns one of the school's student prizes, they show up here so you can hand it out.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">No one in this class has earned a student prize yet.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {waiting > 0 && (
        <Notice tone="warning">
          {waiting === 1 ? "1 prize is" : `${waiting} prizes are`} ready to hand out.
        </Notice>
      )}
      {prizes.map((p) => {
        if (p.award_style === "raffle") return <DrawPrize key={p.id} prize={p} />;
        if (p.award_style === "top_in_class") return <TopOfClassPrize key={p.id} prize={p} />;
        return <UnlockPrize key={p.id} prize={p} />;
      })}
    </div>
  );
}

function UnlockPrize({ prize }) {
  const pending = prize.achievements.filter((a) => !a.disbursed);
  const handedOut = prize.achievements.filter((a) => a.disbursed);
  const pct = percentOf(prize.disbursed_count, prize.achieved_count);

  return (
    <PrizeCard prize={prize} stat={<HandedOutStat handedOut={prize.disbursed_count} earned={prize.achieved_count} />}>
      {prize.achieved_count > 0 && (
        <ProgressBar pct={pct} footnote={`${pct}% handed out`} barColor="bg-primary" footnoteColor="text-primary" />
      )}
      {pending.length > 0 && (
        <Group title="Ready to hand out" action={<HandOutAllButton path={prize.disburse_all_path} count={pending.length} prizeName={prize.prize} />}>
          {pending.map((a) => <HandOutRow key={a.id} achievement={a} prizeName={prize.prize} />)}
        </Group>
      )}
      {handedOut.length > 0 && (
        <Group title="Handed out">
          {handedOut.map((a) => <HandOutRow key={a.id} achievement={a} prizeName={prize.prize} />)}
        </Group>
      )}
    </PrizeCard>
  );
}

function DrawPrize({ prize }) {
  const winners = prize.achievements.filter((a) => a.won);

  return (
    <PrizeCard
      prize={prize}
      description={`${prize.kind === "money" ? "Money raised" : "Minutes read"} earn entries in the draw. Your school admin draws winners after the read-a-thon.`}
      stat={prize.total_entries > 0 && (
        <>
          <p className="font-display text-2xl font-semibold leading-none font-tabular">{formatNumber(prize.total_entries)}</p>
          <p className="mt-1 text-xs text-muted-foreground">entries · {pluralize(prize.entrants_count, "student")}</p>
        </>
      )}
    >
      {winners.length === 0 ? (
        <p className="text-sm text-muted-foreground">No winners from this class yet.</p>
      ) : (
        <Group title={winners.length === 1 ? "Winner" : "Winners"}>
          {winners.map((a) => <HandOutRow key={a.id} achievement={a} prizeName={prize.prize} />)}
        </Group>
      )}
    </PrizeCard>
  );
}

function TopOfClassPrize({ prize }) {
  const who = topInClassLabel(prize.kind).toLowerCase();
  const perClass = prize.competes_within === "classroom";
  const where = topStudentGroupWhere(prize) ?? "among classes with no division";

  return (
    <PrizeCard
      prize={prize}
      description={perClass
        ? (prize.finalized ? `This class's ${who} won this prize.` : `Goes to this class's ${who} when the campaign finishes. Ties all win.`)
        : (prize.finalized ? `Went to the ${who} ${where}.` : `Goes to the ${who} ${where} when the campaign finishes. Ties all win.`)}
    >
      {prize.finalized ? (
        prize.achievements.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {perClass ? "No one in this class qualified." : prize.leading_elsewhere ? `The winner ${where} is in another class.` : "No one qualified."}
          </p>
        ) : (
          <Group title={prize.achievements.length === 1 ? "Winner" : "Winners (tied)"}>
            {prize.achievements.map((a) => <HandOutRow key={a.id} achievement={a} prizeName={prize.prize} />)}
          </Group>
        )
      ) : prize.leaders.length === 0 ? (
        <div className="space-y-1 text-sm text-muted-foreground">
          <p>{prize.leading_elsewhere ? `The current leader ${where} is in another class.` : "No one has qualified yet."}</p>
          {prize.class_best && (
            <p>
              This class's {who}:{" "}
              {prize.class_best.students.map((s, i) => (
                <span key={s.id}>{i > 0 && ", "}<StudentLink name={s.student_name} href={s.student_path} className="text-foreground" /></span>
              ))}
              {" "}· {formatPrizeAmount(prize.kind, prize.class_best.amount)}
              {prize.leading_elsewhere && `, ${ordinal(prize.class_best.rank)} ${where}, ${formatPrizeAmount(prize.kind, prize.class_best.gap_to_first)} behind 1st`}
            </p>
          )}
        </div>
      ) : (
        <Group title={prize.leaders.length === 1 && !prize.leading_elsewhere ? "Leading now" : "Tied for the lead"}>
          {prize.leaders.map((l) => (
            <li key={l.id} className="flex items-center justify-between gap-3 px-4 py-3">
              <StudentLink name={l.student_name} href={l.student_path} className="font-medium" />
              <span className="text-sm text-muted-foreground font-tabular">{formatPrizeAmount(prize.kind, prize.leader_amount)}</span>
            </li>
          ))}
        </Group>
      )}
    </PrizeCard>
  );
}

function Group({ title, action, children }) {
  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</p>
        {action}
      </div>
      <ul className="divide-y divide-border/60 rounded-lg border border-border">{children}</ul>
    </div>
  );
}
